import type { ProductInfo } from "@expert/contentful";
import type { Partner } from "@expert/shared-types";
import { Badge, Card, Center, Flex, Group, Image, Space, Stack, Text } from "@mantine/core";
import { useCallDetailsStore } from "@/call-details";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { type EligibleProduct, useProductOfferStore } from "@/sales-tools/home-product";
import { SaleProgress } from "@/sales-tools/home-product/types";
import { DeclinedIcon } from "@/sales-tools/icons/DeclinedIcon";
import { EnrolledIcon } from "@/sales-tools/icons/EnrolledIcon";
import { useSessionId } from "@/sdk";
import classes from "./PlanCard.module.css";

export type PlanCardVariant = "full" | "compact";

/** Plan badge component that can be used for either recommended or eligibility badge */
const PlanBadge = ({ text, testId }: { text: string; testId?: string }) => (
    <Badge data-testid={testId} className={classes.badge} autoContrast>
        {text}
    </Badge>
);

/** Status indicator showing enrollment status (Enrolled/Declined) */
const StatusIndicator = ({ saleProgress }: { saleProgress: SaleProgress }) => {
    if (([SaleProgress.InProgress, SaleProgress.Failure] as SaleProgress[]).includes(saleProgress)) {
        return null;
    }
    return (
        <Flex className={classes.statusIndicator}>
            {saleProgress === SaleProgress.Success ? (
                <EnrolledIcon data-testid="enrolled-icon" height="15px" width="15px" />
            ) : (
                <DeclinedIcon data-testid="declined-icon" height="15px" width="15px" />
            )}
            <Text size="xs">{saleProgress === SaleProgress.Success ? "Enrolled" : "Declined"}</Text>
        </Flex>
    );
};

/** Plan card props */
export interface PlanCardProps extends Omit<React.ComponentPropsWithoutRef<"div">, "onClick"> {
    saleProgress: SaleProgress;
    partner: Partner;
    product: ProductInfo;
    onClick: () => void;
    variant?: PlanCardVariant;
    /**
     * Controls which badge to show:
     * - When true: Shows "Eligible" badge
     * - When false: Shows "Recommended" badge if conditions are met
     */
    showEligibleTag?: boolean;
    /**
     * Position of the product in the list (0-indexed).
     * Used to determine if this is the first product for recommended badge logic.
     */
    position?: number;
}

/**
 * Plan card component displaying product information with enrollment status.
 * Supports "full" (default) and "compact" variants
 */
export const PlanCard = ({
    saleProgress,
    partner,
    product: { displayName, tagline, logo, partnerLogo, isRecommended, productSku },
    onClick,
    variant = "compact",
    showEligibleTag = false,
    position,
}: PlanCardProps) => {
    const sessionId = useSessionId();
    const products = useProductOfferStore.getState().getProducts(sessionId);
    const { CLIENT } = useCallDetailsStore.getState().callDetails;

    const { isSalesQuestionnaireEnabled = false } = usePartnerFeatureConfigured();

    const override = isRecommendedOverrides(products, CLIENT, productSku);
    const isProductRecommended =
        override !== undefined ? override : (position === 0 && isSalesQuestionnaireEnabled) || isRecommended;

    const isClickable = saleProgress === SaleProgress.InProgress && typeof onClick === "function";
    const handleClick = isClickable ? onClick : undefined;

    const uniqueId = `${displayName}-card`;

    return (
        <Card
            data-testid={uniqueId}
            id={uniqueId}
            key={uniqueId}
            data-sales-progress={saleProgress}
            data-variant={variant}
            onClick={handleClick}
            className={classes.planCardHover}
            radius="md"
            aria-label={uniqueId}
        >
            <Group wrap="nowrap" justify="space-between">
                <Stack gap="xxs">
                    {saleProgress === SaleProgress.InProgress && showEligibleTag && (
                        <Group gap="xxs" align="flex-start">
                            <PlanBadge text="Eligible" testId={`${displayName}-eligible-badge`} />
                        </Group>
                    )}
                    <Flex align="flex-start">
                        <Image
                            w="auto"
                            classNames={{ root: classes.planNameRoot }}
                            data-testid={`${partner}-logo`}
                            alt={`${partner}-logo`}
                            src={partnerLogo}
                        />
                        {/* FIXME: long displayNames overflow to the next line and make the card taller.
                        This line will not flow around the productImage */}
                        {variant !== "full" && (
                            <>
                                <Space w="xxs" />
                                <Text size="sm"> | {displayName}</Text>
                            </>
                        )}
                    </Flex>
                    <Text data-testid={`${displayName}-tagline`} c="dark.2" lh="1rem" size="xs">
                        {tagline}
                    </Text>
                    {saleProgress === SaleProgress.InProgress && isProductRecommended && !showEligibleTag && (
                        <Group gap="xxs" align="flex-start">
                            <PlanBadge text="Recommended" testId={`${displayName}-recommended-badge`} />
                        </Group>
                    )}
                </Stack>
                {logo && (
                    <Center>
                        <Image
                            className={classes.productImage}
                            src={logo}
                            data-testid={`${displayName}-image`}
                            alt={`${displayName} product image`}
                        />
                    </Center>
                )}
            </Group>
            <StatusIndicator saleProgress={saleProgress} />
        </Card>
    );
};

function isRecommendedOverrides(
    products: EligibleProduct[],
    client?: string,
    productSku?: string,
): boolean | undefined {
    if (client === "ATT_IBC") {
        //for ATT_IBC calls, only in the case where the customer is a non-subscriber will we ever sort PA1 to be the first element returned from eligibility.
        const firstProduct = products.at(0);
        if (firstProduct?.productSku === "ATT_PA1_NOT_WITH_AAL") {
            return productSku === "ATT_PA1_NOT_WITH_AAL";
        }
    }
    return undefined;
}
